import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { MODEL_DRAW_WIDTH } from 'config';
import { CONTENT_WITH_SUB_NAV_OFFSET, CONTENT_WITH_SUB_NAV_MOBILE_OFFSET } from 'components/Layout/Layout';
import { DufBulkImportGeometryEntityDto, DufLayerRefDto } from 'providers/api';

interface DufStopeGeometryDialogProps {
  open: boolean;
  isParsing: boolean;
  isImporting: boolean;
  error: string | null;
  isImperial: boolean;
  layers: DufLayerRefDto[];
  availableAttributes: string[];
  uniqueValues: string[];
  matchingEntities: DufBulkImportGeometryEntityDto[];
  selectedLayerGuid: string;
  selectedAttribute: string;
  selectedValue: string;
  onLayerChange: (layerGuid: string) => void;
  onAttributeChange: (attribute: string) => void;
  onValueChange: (value: string) => void;
  onImport: () => void;
  onCancel: () => void;
}

// This panel temporarily replaces the stope sidebar, so it must clear exactly what ModelDrawer
// clears: the app bar + sub-nav, AND the stopes ribbon toolbar. The ribbon collapses
// Office-style, so its height is dynamic and published as --stopes-toolbar-height rather than
// being a number anyone can hardcode - which is what the old 128px constant tried to do, and
// why the panel header ended up behind the ribbon.
const TOP = `calc(${CONTENT_WITH_SUB_NAV_OFFSET}px + var(--stopes-toolbar-height, 0px))`;
const HEIGHT = `calc(100% - ${CONTENT_WITH_SUB_NAV_OFFSET}px - var(--stopes-toolbar-height, 0px))`;
const MOBILE_TOP = `calc(${CONTENT_WITH_SUB_NAV_MOBILE_OFFSET}px + var(--stopes-toolbar-height, 0px))`;
const MOBILE_HEIGHT = `calc(100% - ${CONTENT_WITH_SUB_NAV_MOBILE_OFFSET}px - var(--stopes-toolbar-height, 0px))`;

const DufStopeGeometryDialog = ({
  open,
  isParsing,
  isImporting,
  error,
  isImperial,
  layers,
  availableAttributes,
  uniqueValues,
  matchingEntities,
  selectedLayerGuid,
  selectedAttribute,
  selectedValue,
  onLayerChange,
  onAttributeChange,
  onValueChange,
  onImport,
  onCancel,
}: DufStopeGeometryDialogProps) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onCancel}
    variant="persistent"
    hideBackdrop
    PaperProps={{
        elevation: 4,
        sx: (theme) => ({
          width: `${MODEL_DRAW_WIDTH}px`,
          top: TOP,
          right: 0,
          height: HEIGHT,
          // Sit above the stope panel (drawer) and the HGL chart overlay
          // (drawer + 1) so this temporarily replaces the stope sidebar.
          zIndex: theme.zIndex.drawer + 2,
          [theme.breakpoints.down('md')]: {
            top: MOBILE_TOP,
            height: MOBILE_HEIGHT,
          },
          pt: 2,
          pb: 2,
          px: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
        }),
      }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="h6">{i18n._('Import Stope Geometry from DUF')}</Typography>
      <IconButton onClick={onCancel} size="small" disabled={isImporting} aria-label="close">
        <CloseIcon />
      </IconButton>
    </Box>

    <Box sx={{ flex: 1, overflow: 'auto' }}>
      {isParsing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={24} />
          <Typography><Trans id="Parsing DUF file...">Parsing DUF file...</Trans></Typography>
        </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <Trans id="The candidate geometry is previewed live in the 3D view beside this panel.">
                The candidate geometry is previewed live in the 3D view beside this panel.
              </Trans>
            </Typography>

            <FormControl fullWidth>
              <InputLabel>{i18n._('Layer')}</InputLabel>
              <Select
                value={selectedLayerGuid}
                label={i18n._('Layer')}
                onChange={(e) => onLayerChange(e.target.value)}
              >
                {layers.map((layer) => (
                  <MenuItem key={layer.guid} value={layer.guid}>
                    {layer.name || layer.guid}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedLayerGuid && availableAttributes.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{i18n._('Attribute')}</InputLabel>
                <Select
                  value={selectedAttribute}
                  label={i18n._('Attribute')}
                  onChange={(e) => onAttributeChange(e.target.value)}
                >
                  {availableAttributes.map((attr) => (
                    <MenuItem key={attr} value={attr}>
                      {attr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedLayerGuid && availableAttributes.length === 0 && (
              <Alert severity={matchingEntities.length > 0 ? 'info' : 'warning'}>
                {matchingEntities.length > 0
                  ? i18n._('No attributes found on this layer. The selected layer will be imported directly.')
                  : (
                    <Trans id="No attributes found on entities in this layer.">
                      No attributes found on entities in this layer.
                    </Trans>
                  )}
              </Alert>
            )}

            {selectedAttribute && uniqueValues.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{i18n._('Value')}</InputLabel>
                <Select
                  value={selectedValue}
                  label={i18n._('Value')}
                  onChange={(e) => onValueChange(e.target.value)}
                >
                  {uniqueValues.map((val) => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {matchingEntities.length > 1 && (
              <Alert severity="warning">
                {selectedValue
                  ? i18n._('Multiple objects found with this value ({count}). Only the first will be imported.', { count: matchingEntities.length })
                  : i18n._('Multiple objects found on this layer ({count}). Only the first will be imported.', { count: matchingEntities.length })}
              </Alert>
            )}

            {matchingEntities.length === 1 && (
              <Typography variant="body2" color="text.secondary">
                {i18n._('1 object found. Type: {type}', { type: matchingEntities[0].type })}
              </Typography>
            )}

            {isImperial && (
              <Alert severity="info">
                <Trans id="This DUF is in imperial units. Geometry and volumes are converted to metric on import.">
                  This DUF is in imperial units. Geometry and volumes are converted to metric on import.
                </Trans>
              </Alert>
            )}

            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        )}
    </Box>

    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Button onClick={onCancel} color="secondary" disabled={isImporting}>
        {i18n._('Cancel')}
      </Button>
      <Button
        onClick={onImport}
        variant="contained"
        disabled={matchingEntities.length === 0 || isImporting || isParsing}
      >
        {isImporting
            ? <CircularProgress size={20} />
            : i18n._('Import')}
      </Button>
    </Box>
  </Drawer>
);

export default DufStopeGeometryDialog;
